import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { MousePointerClick, Users, Percent, Megaphone, RefreshCw, Lock, LogOut, BarChart3, FileText } from "lucide-react";
import { ArticlesManager } from "../components/admin/ArticlesManager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "vr_admin_token";

const StatCard = ({ icon: Icon, label, value, sub }) => (
  <div className="border border-slate-200 rounded-lg p-6 bg-white">
    <div className="flex items-center gap-2 text-slate-500">
      <Icon className="w-4 h-4 text-[#5A31F4]" />
      <span className="text-xs font-bold uppercase tracking-[0.15em]">{label}</span>
    </div>
    <p className="mt-3 font-heading text-3xl font-black text-[#0F172A]">{value}</p>
    {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
  </div>
);

const RankTable = ({ title, rows, testId }) => (
  <div className="border border-slate-200 rounded-lg bg-white overflow-hidden" data-testid={testId}>
    <h3 className="font-heading font-bold text-[#0F172A] px-5 py-4 border-b border-slate-200">{title}</h3>
    <table className="w-full text-sm">
      <tbody>
        {rows.length === 0 && <tr><td className="px-5 py-4 text-slate-400">No data yet</td></tr>}
        {rows.map((r) => (
          <tr key={r.key} className="border-b border-slate-100 last:border-0">
            <td className="px-5 py-2.5 text-slate-700 font-medium">{r.key}</td>
            <td className="px-5 py-2.5 text-right font-bold text-[#0F172A]">{r.count}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const isoDaysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};
const today = () => new Date().toISOString().slice(0, 10);

const PRESETS = [
  { id: "all", label: "All time" },
  { id: "today", label: "Today" },
  { id: "7d", label: "Last 7 days" },
  { id: "30d", label: "Last 30 days" },
  { id: "custom", label: "Custom" },
];

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
};

const LoginForm = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-sm mx-auto mt-16 bg-white border border-slate-200 rounded-lg p-8" data-testid="admin-login-form">
      <div className="flex items-center gap-2 text-[#0F172A]">
        <Lock className="w-5 h-5 text-[#5A31F4]" />
        <h1 className="font-heading text-xl font-bold" data-testid="admin-login-title">Admin access</h1>
      </div>
      <p className="mt-2 text-sm text-slate-600">Enter the admin password to view the traffic dashboard.</p>
      <form onSubmit={submit} className="mt-6 space-y-4">
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Admin password"
          data-testid="admin-password-input"
          className="w-full border border-slate-300 rounded-md px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#5A31F4] focus:border-transparent"
          autoFocus
        />
        {error && <p className="text-sm text-red-600" data-testid="admin-login-error">{error}</p>}
        <button
          type="submit"
          disabled={busy || !password}
          data-testid="admin-login-submit"
          className="w-full bg-[#5A31F4] text-white hover:bg-[#4B29CD] disabled:opacity-50 rounded-md px-4 py-3 text-sm font-bold transition-all"
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [tab, setTab] = useState("analytics");
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(false);
  const [preset, setPreset] = useState("all");
  const [customFrom, setCustomFrom] = useState(isoDaysAgo(7));
  const [customTo, setCustomTo] = useState(today());

  const rangeFor = useCallback((p) => {
    if (p === "today") return { date_from: today(), date_to: today() };
    if (p === "7d") return { date_from: isoDaysAgo(6), date_to: today() };
    if (p === "30d") return { date_from: isoDaysAgo(29), date_to: today() };
    if (p === "custom") return { date_from: customFrom || undefined, date_to: customTo || undefined };
    return {};
  }, [customFrom, customTo]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setStats(null);
  };

  const load = useCallback((p) => {
    setStats(null);
    setError(false);
    axios
      .get(`${API}/admin/stats`, { params: rangeFor(p ?? preset), headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } })
      .then((r) => setStats(r.data))
      .catch((err) => {
        if (err.response?.status === 401) logout();
        else setError(true);
      });
  }, [preset, rangeFor]);

  useEffect(() => {
    if (token && preset !== "custom") load(preset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, preset]);

  return (
    <main className="bg-[#F8FAFC] min-h-screen" data-testid="admin-page">
      <Helmet>
        <title>Traffic & CTA Dashboard | VoiceRanker.ai</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {!token ? (
          <LoginForm onLogin={setToken} />
        ) : (
          <>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Admin</p>
                <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-[#0F172A]" data-testid="admin-title">
                  {tab === "analytics" ? "Traffic & CTA Dashboard" : "Article Manager"}
                </h1>
                {tab === "analytics" && (
                  <p className="mt-2 text-sm text-slate-600 max-w-2xl">Visits, affiliate CTA clicks, and paid-ads keyword attribution (from utm_term / gclid). Add <code className="bg-slate-200 px-1 rounded">utm_term={"{keyword}"}</code> to your Google Ads final URL suffix to capture keywords.</p>
                )}
                {tab === "articles" && (
                  <p className="mt-2 text-sm text-slate-600 max-w-2xl">Create daily or weekly articles — write manually or generate an AI draft, edit, and publish straight to the blog.</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {tab === "analytics" && (
                  <button onClick={() => load()} data-testid="admin-refresh-btn" className="inline-flex items-center gap-2 bg-[#1A56DB] text-white hover:bg-[#1547B1] rounded-md px-4 py-2 text-sm font-bold transition-all">
                    <RefreshCw className="w-4 h-4" /> Refresh
                  </button>
                )}
                <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 border border-slate-300 text-slate-600 hover:border-slate-500 rounded-md px-4 py-2 text-sm font-bold transition-all">
                  <LogOut className="w-4 h-4" /> Log out
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2 mt-8 border-b border-slate-200" data-testid="admin-tabs">
              {[["analytics", "Analytics", BarChart3], ["articles", "Articles", FileText]].map(([id, label, Icon]) => (
                <button
                  key={id}
                  onClick={() => setTab(id)}
                  data-testid={`admin-tab-${id}`}
                  className={`inline-flex items-center gap-2 px-5 py-3 text-sm font-bold border-b-2 -mb-px transition-all ${tab === id ? "border-[#5A31F4] text-[#5A31F4]" : "border-transparent text-slate-500 hover:text-slate-800"}`}
                >
                  <Icon className="w-4 h-4" /> {label}
                </button>
              ))}
            </div>

            {tab === "articles" && <ArticlesManager onAuthFail={logout} />}

            {tab === "analytics" && (
              <>
            <div className="flex flex-wrap items-center gap-2 mt-8" data-testid="admin-date-filters">
              {PRESETS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setPreset(p.id)}
                  data-testid={`admin-filter-${p.id}`}
                  className={`rounded-md px-4 py-2 text-sm font-bold transition-all ${preset === p.id ? "bg-[#0F172A] text-white" : "bg-white border border-slate-300 text-slate-600 hover:border-slate-500"}`}
                >
                  {p.label}
                </button>
              ))}
              {preset === "custom" && (
                <div className="flex items-center gap-2 ml-2" data-testid="admin-custom-range">
                  <input type="date" value={customFrom} max={customTo} onChange={(e) => setCustomFrom(e.target.value)} data-testid="admin-date-from" className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white" />
                  <span className="text-slate-500 text-sm">to</span>
                  <input type="date" value={customTo} min={customFrom} onChange={(e) => setCustomTo(e.target.value)} data-testid="admin-date-to" className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white" />
                  <button onClick={() => load("custom")} data-testid="admin-apply-custom" className="bg-[#5A31F4] text-white hover:bg-[#4B29CD] rounded-md px-4 py-2 text-sm font-bold transition-all">Apply</button>
                </div>
              )}
            </div>

            {error && <p className="mt-8 text-red-600" data-testid="admin-error">Failed to load stats.</p>}
            {!stats && !error && <p className="mt-8 text-slate-500" data-testid="admin-loading">Loading…</p>}

            {stats && (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8" data-testid="admin-stat-cards">
                  <StatCard icon={Users} label="Unique visitors" value={stats.totals.visits} sub={`${stats.totals.paid_visits} from paid ads`} />
                  <StatCard icon={MousePointerClick} label="CTA Clicks" value={stats.totals.clicks} sub={`${stats.totals.paid_clicks} from paid ads`} />
                  <StatCard icon={Percent} label="Click-through rate" value={`${stats.totals.ctr}%`} sub="Clicks / visits" />
                  <StatCard icon={Megaphone} label="Keywords tracked" value={stats.keywords.filter((k) => k.keyword !== "(none)").length} sub="From utm_term" />
                </div>

                <div className="border border-slate-200 rounded-lg bg-white overflow-x-auto mt-8" data-testid="admin-keywords-table">
                  <h3 className="font-heading font-bold text-[#0F172A] px-5 py-4 border-b border-slate-200">Paid Keywords → Visits & CTA Clicks</h3>
                  <table className="w-full text-sm min-w-[560px]">
                    <thead>
                      <tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                        <th className="text-left px-5 py-3 font-bold">Keyword (utm_term)</th>
                        <th className="text-right px-5 py-3 font-bold">Visits</th>
                        <th className="text-right px-5 py-3 font-bold">CTA Clicks</th>
                        <th className="text-right px-5 py-3 font-bold">CTR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.keywords.length === 0 && <tr><td colSpan="4" className="px-5 py-4 text-slate-400">No data yet</td></tr>}
                      {stats.keywords.map((k) => (
                        <tr key={k.keyword} className="border-t border-slate-100">
                          <td className="px-5 py-2.5 font-medium text-slate-700">{k.keyword}</td>
                          <td className="px-5 py-2.5 text-right">{k.visits}</td>
                          <td className="px-5 py-2.5 text-right">{k.clicks}</td>
                          <td className="px-5 py-2.5 text-right font-bold text-[#0F172A]">{k.ctr === null ? "—" : `${k.ctr}%`}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="border border-slate-200 rounded-lg bg-white overflow-x-auto mt-8" data-testid="admin-articles-performance">
                  <h3 className="font-heading font-bold text-[#0F172A] px-5 py-4 border-b border-slate-200">Performance Per Article</h3>
                  <table className="w-full text-sm min-w-[640px]">
                    <thead>
                      <tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                        <th className="text-left px-5 py-3 font-bold">Article</th>
                        <th className="text-left px-5 py-3 font-bold">Published</th>
                        <th className="text-right px-5 py-3 font-bold">Views</th>
                        <th className="text-right px-5 py-3 font-bold">CTA Clicks</th>
                        <th className="text-right px-5 py-3 font-bold">CTR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(stats.articles || []).length === 0 && <tr><td colSpan="5" className="px-5 py-4 text-slate-400">No articles yet</td></tr>}
                      {(stats.articles || []).map((a) => (
                        <tr key={a.slug} className="border-t border-slate-100" data-testid={`article-perf-${a.slug}`}>
                          <td className="px-5 py-2.5 font-medium text-slate-700">
                            <a href={`/blog/${a.slug}`} target="_blank" rel="noreferrer" className="hover:text-[#1A56DB]">{a.title}</a>
                          </td>
                          <td className="px-5 py-2.5 text-slate-500 whitespace-nowrap">{a.published}</td>
                          <td className="px-5 py-2.5 text-right">{a.views}</td>
                          <td className="px-5 py-2.5 text-right">{a.clicks}</td>
                          <td className="px-5 py-2.5 text-right font-bold text-[#0F172A]">{a.ctr === null ? "—" : `${a.ctr}%`}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
                  <RankTable title="Clicks by CTA placement" rows={stats.by_cta} testId="admin-cta-table" />
                  <RankTable title="Visits by source" rows={stats.by_source} testId="admin-source-table" />
                  <RankTable title="Visits by campaign" rows={stats.by_campaign} testId="admin-campaign-table" />
                </div>

                <div className="border border-slate-200 rounded-lg bg-white overflow-x-auto mt-8" data-testid="admin-recent-clicks">
                  <h3 className="font-heading font-bold text-[#0F172A] px-5 py-4 border-b border-slate-200">Recent CTA Clicks</h3>
                  <table className="w-full text-sm min-w-[720px]">
                    <thead>
                      <tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                        <th className="text-left px-5 py-3 font-bold">Time (UTC)</th>
                        <th className="text-left px-5 py-3 font-bold">CTA</th>
                        <th className="text-left px-5 py-3 font-bold">Page</th>
                        <th className="text-left px-5 py-3 font-bold">Keyword</th>
                        <th className="text-left px-5 py-3 font-bold">Source</th>
                        <th className="text-left px-5 py-3 font-bold">Paid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.recent_clicks.length === 0 && <tr><td colSpan="6" className="px-5 py-4 text-slate-400">No clicks recorded yet</td></tr>}
                      {stats.recent_clicks.map((c, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="px-5 py-2.5 text-slate-500 whitespace-nowrap">{(c.ts || "").replace("T", " ").slice(0, 19)}</td>
                          <td className="px-5 py-2.5 font-medium text-slate-700">{c.cta_id}</td>
                          <td className="px-5 py-2.5 text-slate-600">{c.page}</td>
                          <td className="px-5 py-2.5">{c.keyword || "—"}</td>
                          <td className="px-5 py-2.5">{c.utm_source || "—"}</td>
                          <td className="px-5 py-2.5">{c.is_paid ? "✓" : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
              </>
            )}
          </>
        )}
      </div>
    </main>
  );
}
